"use client";

import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";

import { useCart as useBackendCart } from "@/contexts/CartContext";
import { getProductBrandLabel, productHasColorVariants, type Product } from "@/lib/product-utils";
import { hydrateProducts } from "@/lib/shop-agent-client";

// Extended type for products with additional fields
type ProductWithExtras = {
  name?: string;
  brand?: string | { name?: string };
  brand_name?: string;
  catalog?: string;
  enrichment?: {
    canonical_brand?: {
      brand_name?: string;
    };
  };
  current_price?: number;
  offers?: {
    lowPrice?: number;
    priceSpecification?: { price?: number };
  };
  image?: string | string[] | { contentUrl?: string };
  color_info?: {
    colors?: unknown[];
  };
};

export interface CartItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
  size?: string;
  color?: string;
  hasColorVariants?: boolean;
  quantity: number;
  retailer: string;
  retailerLogo?: string;
  uuid?: string; // Keep reference to the actual product UUID
  catalog?: string;
}

interface ShoppingCartContextType {
  items: CartItem[];
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  removeFromCart?: (id: string) => void; // Alias for ManualCheckoutModal
  addToCart?: (item: CartItem) => void; // For undo functionality
  getItemsByRetailer: () => Record<string, CartItem[]>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getRetailerSubtotal: (retailer: string) => number;
  loading: boolean;
}

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(undefined);

export function ShoppingCartProvider({ children }: { children: ReactNode }) {
  const backendCart = useBackendCart();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Convert backend cart items to CartItem format
  useEffect(() => {
    const convertCartItems = async () => {
      // Avoid flashing empty state while auth/cart are still loading
      if (backendCart.loading) {
        return;
      }
      if (!backendCart.defaultCart || backendCart.defaultCart.product_ids.length === 0) {
        setItems([]);
        return;
      }

      setLoadingProducts(true);
      const convertedItems: CartItem[] = [];
      // One batched request for the whole cart; results follow product_ids.
      let hydrated: Awaited<ReturnType<typeof hydrateProducts>>;
      try {
        hydrated = await hydrateProducts(backendCart.defaultCart.product_ids);
      } catch (error) {
        // An outage keeps the items already shown; it does not clear the cart.
        console.error("Failed to hydrate cart products:", error);
        setLoadingProducts(false);
        return;
      }

      for (let i = 0; i < backendCart.defaultCart.product_ids.length; i++) {
        const productId = backendCart.defaultCart.product_ids[i];
        const variant = backendCart.defaultCart.product_variants?.[i];

        try {
          const product = hydrated[i]?.product;
          if (product) {
            const productWithExtras = product as unknown as ProductWithExtras;

            const brandLabel =
              getProductBrandLabel(productWithExtras as unknown as Product) || "Unknown Brand";

            // Extract price (use normalized field only, no offers fallback)
            const price =
              typeof productWithExtras.current_price === "number"
                ? productWithExtras.current_price
                : 0;

            // Extract image
            let image = "";
            if (productWithExtras.image) {
              if (typeof productWithExtras.image === "string") {
                image = productWithExtras.image;
              } else if (
                Array.isArray(productWithExtras.image) &&
                productWithExtras.image.length > 0
              ) {
                image = productWithExtras.image[0];
              } else if (
                typeof productWithExtras.image === "object" &&
                !Array.isArray(productWithExtras.image) &&
                productWithExtras.image.contentUrl
              ) {
                image = productWithExtras.image.contentUrl;
              }
            }

            convertedItems.push({
              id: productId,
              uuid: productId,
              name: product.name || "Unknown Product",
              brand: brandLabel,
              price: price,
              image: image || "/api/placeholder/120/160",
              size: variant?.size || undefined,
              color: variant?.color || undefined,
              hasColorVariants: productHasColorVariants(productWithExtras as unknown as Product),
              quantity: 1, // Backend doesn't support quantity per item yet
              retailer: brandLabel,
              catalog: productWithExtras.catalog || undefined,
            });
          }
        } catch (error) {
          console.error("Failed to hydrate product:", productId, error);
        }
      }

      setItems(convertedItems);
      setLoadingProducts(false);
    };

    convertCartItems();
  }, [backendCart.defaultCart, backendCart.loading]);

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const removeItem = useCallback(
    async (id: string) => {
      // Remove from local state immediately for responsiveness
      setItems((prevItems) => prevItems.filter((item) => item.id !== id));

      // Also remove from backend
      try {
        await backendCart.removeItem(id);
      } catch (error) {
        console.error("Failed to remove item from backend:", error);
        // Restore item on error
        backendCart.refreshCarts();
      }
    },
    [backendCart]
  );

  const addToCart = useCallback(
    (item: CartItem) => {
      // For undo functionality - add back to local state
      setItems((prevItems) => [...prevItems, item]);

      // Also add to backend if it has a UUID
      if (item.uuid) {
        backendCart
          .addItem(item.uuid, {
            size: item.size,
            color: item.color,
          })
          .catch((error) => {
            console.error("Failed to add item back to backend:", error);
          });
      }
    },
    [backendCart]
  );

  const getItemsByRetailer = () => {
    return items.reduce(
      (acc, item) => {
        if (!acc[item.retailer]) {
          acc[item.retailer] = [];
        }
        acc[item.retailer].push(item);
        return acc;
      },
      {} as Record<string, CartItem[]>
    );
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getRetailerSubtotal = (retailer: string) => {
    return items
      .filter((item) => item.retailer === retailer)
      .reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const value: ShoppingCartContextType = {
    items,
    updateQuantity,
    removeItem,
    removeFromCart: removeItem, // Alias for ManualCheckoutModal compatibility
    addToCart,
    getItemsByRetailer,
    getTotalItems,
    getTotalPrice,
    getRetailerSubtotal,
    loading: backendCart.loading || loadingProducts,
  };

  return <ShoppingCartContext.Provider value={value}>{children}</ShoppingCartContext.Provider>;
}

// Export with different name to avoid conflicts
export function useShoppingCart() {
  const context = useContext(ShoppingCartContext);
  if (context === undefined) {
    throw new Error("useShoppingCart must be used within a ShoppingCartProvider");
  }
  return context;
}
